import Alert from "@mui/material/Alert";
import React, {useState, useEffect, useRef} from "react";

import {Form, Formik} from "formik";
import * as Yup from "yup";
import {GoogleAddress} from "../components/GoogleAddress";
import {BusinessVerifyService, BusinessVerifyWithPhoneService, GetProfileDetails, SendEmailChangeOtp, SendPhoneChangeOtp, VerifyEmailChangeOtp, VerifyPhoneChangeOtp} from "../services/auth";
import {sweetAlerts} from "../utils/sweet-alerts";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

// keep a static +1 prefix; user can only edit digits after +1
const normalizePhoneInput = (val) => {
	if (val === undefined || val === null) return "+1";
	const digits = val.toString().replace(/[^\d]/g, "");
	// always ensure it starts with country code 1
	const rest = digits.startsWith("1") ? digits.slice(1) : digits;
	return "+1" + rest;
};

const CustomBusinessVerified = ({skipButton = false, cbAction}) => {
	const [uuid, setUuid] = useState(null);

	const [userDetails, setUserDetails] = useState({});

	const [gmailSendLoading, setGmailSendLoading] = useState(false);
	const [phoneSendLoading, setPhoneSendLoading] = useState(false);

	const [otpModal, setOtpModal] = useState(false);
	const [verifyLoading, setVerifyLoading] = useState(false);
	const [verifySuccess, setVerifySuccess] = useState(null);
	const [verifyError, setVerifyError] = useState(null);
	const [verifyMessage, setVerifyMessage] = useState(null);
	const [otp, setOtp] = useState(null);
	const [values, setValues] = useState(null);
	const formikRef = useRef();

	const handleSendOtp = async (target, value) => {
		// basic required check
		if (!value || !value.toString().trim()) {
			setOtpModal((prev) => ({
				...prev,
				open: true,
				target,
				value,
				error: `Please enter a ${target === "email" ? "new email" : "new phone number"} first.`,
				success: null,
				sending: false,
				verifying: false,
			}));
			return;
		}

		if (target === "email") {
			setGmailSendLoading(true);
		} else {
			setPhoneSendLoading(true);
		}

		const apiFn = target === "email" ? SendEmailChangeOtp : SendPhoneChangeOtp;
		const payload = target === "email" ? {new_email: value} : {new_phone: value};

		try {
			const response = await apiFn(payload);

			if (response?.status) {
				setOtpModal({
					open: true,
					target,
					value,
					otp: "",
					sending: false,
					verifying: false,
					error: null,
					success: `Code sent successfully to your ${target === "email" ? "email" : "phone"}.`,
				});
			} else {
				setOtpModal((prev) => ({
					...prev,
					open: true,
					target,
					value,
					sending: false,
					verifying: false,
					error: response?.message || "Failed to send Code. Please try again.",
					success: null,
				}));
			}
		} catch (err) {
			setOtpModal((prev) => ({
				...prev,
				open: true,
				target,
				value,
				sending: false,
				verifying: false,
				error: "Failed to send Code. Please try again.",
				success: null,
			}));
		} finally {
			if (target === "email") {
				setGmailSendLoading(false);
			} else {
				setPhoneSendLoading(false);
			}
		}
	};

	const handleVerifyOtp = async () => {
		if (!otpModal.otp) {
			setOtpModal((prev) => ({...prev, error: "Please enter the Code."}));
			return;
		}

		setOtpModal((prev) => ({...prev, verifying: true, error: null, success: null}));

		const isEmail = otpModal.target === "email";
		const apiFn = isEmail ? VerifyEmailChangeOtp : VerifyPhoneChangeOtp;
		const payload = isEmail ? {new_email: otpModal.value, otp: otpModal.otp} : {new_phone: otpModal.value, otp: otpModal.otp};

		try {
			const response = await apiFn(payload);
			if (response?.status) {
				if (isEmail) {
					setUserDetails((prev) => ({...prev, email: otpModal.value}));
					formikRef.current?.setFieldValue("businessEmail", otpModal.value);
					formikRef.current?.setFieldValue("isEmailEditing", false);
				} else {
					setUserDetails((prev) => ({...prev, phone: otpModal.value}));
					formikRef.current?.setFieldValue("businessPhone", otpModal.value);
					formikRef.current?.setFieldValue("isPhoneEditing", false);
				}
				setOtpModal({
					open: false,
					target: null,
					value: "",
					otp: "",
					sending: false,
					verifying: false,
					error: null,
					success: null,
				});
			} else {
				setOtpModal((prev) => ({
					...prev,
					verifying: false,
					error: response?.message || "Code verification failed. Please try again.",
				}));
			}
		} catch (err) {
			setOtpModal((prev) => ({
				...prev,
				verifying: false,
				error: "Code verification failed. Please try again.",
			}));
		}
	};

	const handleBusinessVerify = async (values) => {
		try {
			setVerifyLoading(true);
			setVerifySuccess(null);
			setVerifyError(null);

			const payload = {
				uuid,
				phone: values?.businessPhone,
				businessName: values?.businessName,
				businessAddress: values?.businessAddress,
				googleListing: values?.googleListing,
			};

			setValues(payload);

			const response = await BusinessVerifyWithPhoneService(payload);

			if (response?.status) {
				setVerifySuccess(response?.message);
				setVerifyError(null);
				setVerifyMessage(response?.message);
				// localStorage.setItem("verified", true);
				sweetAlerts("success", response?.message);
				setOtpModal(true);
			} else {
				setVerifyError(response?.message || "Verification failed. Please check your details.");
				sweetAlerts("error", response?.message || "Verification failed. Please check your details.");
				setVerifySuccess(null);
			}
		} catch (err) {
			setVerifyError("Something went wrong. Please try again.");
			setVerifySuccess(null);
		} finally {
			setVerifyLoading(false);
		}
	};

	useEffect(() => {
		let isMounted = true;
		const loadProfile = async () => {
			try {
				const response = await GetProfileDetails();
				if (!isMounted) return;
				if (response.status) {
					setUserDetails(response?.data);
					setUuid(response?.data?.user_uuid);
				}
			} catch (error) {
				// silently ignore
			}
		};
		loadProfile();
		return () => {
			isMounted = false;
		};
	}, []);

	const closeOtpModal = () => {
		setOtpModal(false);
	};

	const handleVerifyBusinessVerify = async () => {
		try {
			setVerifyLoading(true);
			setVerifySuccess(null);
			setVerifyError(null);

			const payload = {
				uuid,
				otp,
				phone: values?.phone,
				businessName: values?.businessName,
				businessAddress: values?.businessAddress,
				googleListing: values?.googleListing,
			};

			const response = await BusinessVerifyWithPhoneService(payload);

			if (response?.status) {
				setVerifySuccess(response?.message);
				setVerifyError(null);
				setVerifyMessage(response?.message);
				localStorage.setItem("verified", true);
				cbAction();
				// sweetAlerts("success", response?.message);
				setOtpModal(true);
			} else {
				setVerifyError(response?.message || "Verification failed. Please check your details.");
				sweetAlerts("error", response?.message || "Verification failed. Please check your details.");
				setVerifySuccess(null);
			}
		} catch (err) {
			setVerifyError("Something went wrong. Please try again.");
			setVerifySuccess(null);
		} finally {
			setVerifyLoading(false);
		}
	};

	return (
		<div className="tab-content-wrapper">
			{verifySuccess && (
				<Alert severity="success" sx={{mb: 2}}>
					{verifySuccess}
				</Alert>
			)}
			{verifyError && (
				<Alert severity="error" sx={{mb: 2}}>
					{verifyError}
				</Alert>
			)}

			<Formik
				enableReinitialize
				innerRef={formikRef}
				initialValues={{
					businessName: userDetails?.business_name || "",
					businessWebsite: userDetails?.business_website || "",
					businessPhone: userDetails?.phone ? normalizePhoneInput(userDetails.phone) : "+1",
					businessEmail: userDetails?.email || "",
					businessAddress: userDetails?.address || "",
					googleListing: userDetails?.business_url || "",
					isPhoneEditing: false,
					isEmailEditing: false,
				}}
				validationSchema={Yup.object({
					businessName: Yup.string().min(2, "Business name is required").required("Business name is required"),
					businessWebsite: Yup.string()
						.nullable()
						.test("valid-business-website", "Enter a valid business website (e.g. xyz.com)", (value) => {
							if (!value) return true; // Allow null / empty
							const pattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/;
							return pattern.test(value);
						}),
					businessPhone: Yup.string()
						.transform((value) => {
							if (!value || value.trim() === "" || value.trim() === "+1") {
								return null;
							}
							return value;
						})
						.nullable()
						.matches(/^\+\d{6,15}$/, "Enter valid phone number with country code (e.g. +14155550123)"),

					businessEmail: Yup.string().email("Enter valid email").required("Business email is required"),
					businessAddress: Yup.string().nullable(),
					googleListing: Yup.string().url("Enter valid Google business link").nullable(),
				})}
				// onSubmit={handleSignup}
			>
				{(props) => {
					const {setFieldValue, handleSubmit, resetForm, dirty, values, errors} = props;
					return (
						<Form noValidate>
							<div className="form-group">
								<label htmlFor="businessName" className="form-label">
									Business Name *
								</label>

								<input
									type="text"
									id="businessName"
									className="form-input"
									placeholder="Enter your Business Name"
									value={values.businessName}
									onChange={(e) => {
										setFieldValue("businessName", e.target.value);
									}}
								/>
								{errors.businessName && <span className="error-text">{errors.businessName}</span>}
							</div>

							<div className="form-group">
								<label htmlFor="businessPhone" className="form-label">
									Business Phone Number <span style={{fontWeight: 100, color: "#8e8e8eff", fontSize: 12}}>(Include country code, e.g. +14155550123)</span>
								</label>
								<div className="d-flex gap-2 flex-wrap">
									<input
										type="text"
										id="businessPhone"
										className={`form-input`}
										value={values.businessPhone}
										onChange={(e) => {
											setFieldValue("businessPhone", normalizePhoneInput(e.target.value));
										}}
									/>
								</div>

								{errors.businessPhone && <span className="error-text">{errors.businessPhone}</span>}
							</div>

							<div className="form-group">
								<label htmlFor="address" className="form-label">
									Business Address <span style={{fontWeight: 100, color: "#8e8e8eff", fontSize: 12}}>(If applicable)</span>{" "}
								</label>
								<GoogleAddress
									styleFlag="loginStyle"
									address={values?.businessAddress}
									callBackAction={(val) => {
										setFieldValue("businessAddress", val.value);
									}}
								/>
								{errors.businessAddress && <span className="error-text">{errors.businessAddress}</span>}
							</div>

							<div className="form-group">
								<label htmlFor="googleListing" className="form-label">
									Google Business Listing <br />
									<span style={{fontWeight: 100, color: "#8e8e8eff", fontSize: 12}}>(Copy and paste your Google Business Listing link here. If your business is not listed in Google, we recommend listing it)</span>
								</label>

								<input
									type="text"
									id="googleListing"
									className="form-input"
									placeholder="Enter your Google Business Listing"
									value={values.googleListing}
									onChange={(e) => {
										setFieldValue("googleListing", e.target.value);
									}}
								/>
								{errors.googleListing && <span className="error-text">{errors.googleListing}</span>}
							</div>

							{skipButton ? (
								<div className="form-row gap-2">
									<button
										type="button"
										className="submit-btn"
										disabled={verifyLoading}
										onClick={() => {
											handleSubmit();
											handleBusinessVerify(values);
										}}>
										{verifyLoading ? "Verifying..." : "Save & Verify"}
									</button>

									<button
										type="button"
										className="submit-btn skip-btn"
										onClick={() => {
											window.location.href = "/contractor-dashboard";
										}}>
										Skip
									</button>
								</div>
							) : (
								<button
									type="button"
									className="submit-btn"
									disabled={verifyLoading}
									onClick={() => {
										handleSubmit();
										handleBusinessVerify(values);
									}}>
									{verifyLoading ? "Verifying..." : "Save & Verify"}
								</button>
							)}
						</Form>
					);
				}}
			</Formik>

			<Dialog open={otpModal} onClose={closeOtpModal}>
				<DialogTitle>Verify Phone</DialogTitle>
				<DialogContent>
					{verifySuccess && (
						<Alert severity="success" sx={{mb: 2}}>
							{verifySuccess}
						</Alert>
					)}
					{verifyError && (
						<Alert severity="error" sx={{mb: 2}}>
							{verifyError}
						</Alert>
					)}
					<DialogContentText>Enter the Code sent to your confirm the new phone number.</DialogContentText>
					<div className="form-group" style={{marginTop: "12px"}}>
						<label className="form-label">Code</label>
						<input
							type="text"
							className="form-input"
							maxLength="6"
							placeholder="123456"
							value={otpModal.otp}
							onChange={(e) => {
								const value = e.target.value.replace(/\D/g, "");
								setOtp(value);
							}}
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeOtpModal} disabled={verifyLoading}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleVerifyBusinessVerify} disabled={verifyLoading}>
						{verifyLoading ? "Verifying..." : "Verify & Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default CustomBusinessVerified;
